import {
  Alert,
  AlertVariantEnum,
  Button,
  ButtonColorEnum,
  ButtonSizeEnum,
  ButtonVariantEnum,
  DateRangeInput,
  DateRangeObject,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  FormLabel,
  ModalDialog,
  ProgressBar,
  TextButtonColorEnum,
  Typography,
} from '@acceso/design-system';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Divider } from '@/components/Divider/Divider';
import { formatDateCustom } from '@/helpers/format-date';
import { createAppTzRangeEdge } from '@/utils/app-tz-date-range';

import {
  TProgramBroadcastBatchProgress,
  TProgramBroadcastBatchResult,
} from '../../hooks/useProgramBroadcastBatch';
import {
  EGenerationBlockReason,
  PROGRAM_BROADCAST_BATCH_CAP,
  useProgramBroadcastGeneration,
} from '../../hooks/useProgramBroadcastGeneration';
import { TProgram } from '../../types';
import {
  MAX_GENERATED_CANDIDATES,
  TProgramBroadcastCandidate,
} from '../../utils/programBroadcastCandidates';

import { GenerationSlotsField } from './GenerationSlotsField';

type TProgramBroadcastGenerationModalProps = {
  isOpen: boolean;
  program: TProgram | null;
  onClose: () => void;
  onGenerate: (candidates: TProgramBroadcastCandidate[], program: TProgram) => void;
  onCancel: () => void;
  onResultStale: () => void;
  isRunning: boolean;
  progress: TProgramBroadcastBatchProgress | null;
  result: TProgramBroadcastBatchResult | null;
};

const describeCandidate = (candidate: TProgramBroadcastCandidate) =>
  `${formatDateCustom({ input: candidate.startDatetime, format: 'dd/MM/yyyy' })} · ${formatDateCustom(
    { input: candidate.startDatetime, format: 'HH:mm' }
  )} - ${formatDateCustom({ input: candidate.endDatetime, format: 'HH:mm' })}`;

export function ProgramBroadcastGenerationModal({
  isOpen,
  program,
  onClose,
  onGenerate,
  onCancel,
  onResultStale,
  isRunning,
  progress,
  result,
}: TProgramBroadcastGenerationModalProps) {
  const { t } = useTranslation();
  const {
    form,
    period,
    onPeriodChange,
    candidates,
    candidatesToCreate,
    skippedCount,
    duplicatesDropped,
    slots,
    slotsMissingHours,
    blockReason,
    canGenerate,
    retryLookup,
  } = useProgramBroadcastGeneration({ program, isOpen });

  // The picker only works in the browser's clock: it is shown the Madrid days of
  // the period and hands back the instants of the Madrid days picked.
  const periodEdge = useMemo(() => createAppTzRangeEdge(period), [period]);

  // One reason is shown at a time, so the whole table is built only when the
  // count it interpolates moves rather than on every render.
  const blockMessages: Record<EGenerationBlockReason, string> = useMemo(
    () => ({
      [EGenerationBlockReason.NoSlots]: t('Añade al menos una franja para generar emisiones'),
      [EGenerationBlockReason.NoPeriod]: t('Selecciona el periodo que quieres generar'),
      [EGenerationBlockReason.NoCandidates]: t(
        'Ninguna franja se emite en los días del periodo seleccionado'
      ),
      [EGenerationBlockReason.SlotsMissingHours]: t(
        'Las franjas necesitan hora de inicio y de fin para generar emisiones'
      ),
      [EGenerationBlockReason.LookupPending]: t('Comprobando qué emisiones ya existen…'),
      [EGenerationBlockReason.LookupTooLarge]: t(
        'El periodo tiene demasiadas emisiones para comprobarlas todas. Acorta el periodo para poder generar.'
      ),
      [EGenerationBlockReason.LookupFailed]: t(
        'No se ha podido comprobar qué emisiones ya existen, así que no se generará ninguna para no duplicarlas'
      ),
      [EGenerationBlockReason.AllAlreadyExist]: t(
        'Todas las emisiones del periodo ya existen: no hay ninguna que crear'
      ),
      [EGenerationBlockReason.OverCap]: t(
        'Se crearían {{count}} emisiones y el máximo por generación es {{cap}}. Acorta el periodo o quita franjas.',
        { count: candidatesToCreate.length, cap: PROGRAM_BROADCAST_BATCH_CAP }
      ),
      [EGenerationBlockReason.TooManyCandidates]: t(
        'El periodo produce más de {{max}} emisiones, demasiadas para comprobarlas y generarlas. Acorta el periodo o quita franjas.',
        { max: MAX_GENERATED_CANDIDATES }
      ),
    }),
    [t, candidatesToCreate.length]
  );

  /**
   * What the run that produced `result` was given. Editing the period or the
   * slots afterwards leaves that report describing a generation nobody asked
   * for any more, next to a preview it hides — so the report goes instead.
   */
  const inputsSignature = useMemo(
    () =>
      JSON.stringify({
        start: period.start?.getTime() ?? null,
        end: period.end?.getTime() ?? null,
        slots: slots.map(slot => [
          slot.start_hour,
          slot.end_hour,
          slot.delivery_hour,
          slot.repetition_day_of_week,
        ]),
      }),
    [period, slots]
  );
  const lastInputsSignature = useRef(inputsSignature);

  useEffect(() => {
    if (lastInputsSignature.current === inputsSignature) return;

    lastInputsSignature.current = inputsSignature;

    if (result && !isRunning) onResultStale();
  }, [inputsSignature, result, isRunning, onResultStale]);

  const handleGenerate = () => {
    if (!program || !canGenerate) return;

    onGenerate(candidatesToCreate, program);
  };

  /**
   * Closing mid-run would hide a loop that keeps creating emissions, so the
   * modal refuses to close until it ends. This covers the dialog's own close
   * button, which `ModalDialog` renders by default, as well as the footer's.
   * The way out of a run is the cancel button, which stops the loop first.
   */
  const handleClose = () => {
    if (isRunning) return;

    onClose();
  };

  // Some slots are unusable but others still produce emissions: the count is
  // right, so this is a warning beside it rather than a reason to block.
  const hasPartiallyIncompleteSlots = slotsMissingHours.length > 0 && candidatesToCreate.length > 0;

  const progressPercentage =
    progress && progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <ModalDialog
      isOpen={isOpen}
      heading={t('Generar emisiones')}
      size="large"
      lineSeparator
      actionLabel={t('Generar')}
      linkLabel={t('Cancelar')}
      closeButton={!isRunning}
      onClose={handleClose}
      onAction={handleGenerate}
      // `actionLabel`, `linkLabel` and `onAction` are what the props union
      // demands of a modal that carries a footer; the custom footer below is
      // what actually renders, and the design system drops the default one.
      customFooter={
        <div className="flex flex-col gap-2">
          <Divider />

          <div className="my-2 flex w-full items-center justify-end gap-4">
            <Button
              variant={ButtonVariantEnum.TEXT}
              color={TextButtonColorEnum.DEFAULT}
              size={ButtonSizeEnum.MD}
              onClick={isRunning ? onCancel : handleClose}
            >
              {isRunning && t('Cancelar generación')}
              {!isRunning && (result ? t('Cerrar') : t('Cancelar'))}
            </Button>

            <Button
              className="w-fit"
              color={ButtonColorEnum.PRIMARY}
              size={ButtonSizeEnum.MD}
              disabled={!canGenerate || isRunning}
              onClick={handleGenerate}
            >
              {isRunning
                ? t('Generando…')
                : t('Generar {{count}} emisiones', { count: candidatesToCreate.length })}
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex w-full flex-col gap-4">
        <div className="flex flex-col gap-1">
          <Typography
            fontSize={FontSizeEnum.Md}
            weight={FontWeightEnum.Bold}
            color={FontColorEnum.Dark10}
          >
            {program?.name}
          </Typography>

          <Typography fontSize={FontSizeEnum.Sm} color={FontColorEnum.NeutralSecondary}>
            {program?.channel?.name}
          </Typography>
        </div>

        <Divider />

        <div className="flex flex-col gap-2">
          <FormLabel htmlFor="schedule">{t('Franjas horarias')}</FormLabel>

          <Alert variant={AlertVariantEnum.INFO} withClose={false}>
            <Typography fontSize={FontSizeEnum.Sm} color={FontColorEnum.Inherit}>
              {t(
                'Los cambios en las franjas solo se aplican a esta generación: no modifican la programación del programa.'
              )}
            </Typography>
          </Alert>

          <div className={isRunning ? 'pointer-events-none opacity-60' : undefined}>
            <GenerationSlotsField form={form} />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <FormLabel htmlFor="period">{t('Periodo a generar')}</FormLabel>

          <DateRangeInput
            className="w-full"
            startRange={periodEdge.passed?.start ?? null}
            endRange={periodEdge.passed?.end ?? null}
            months={2}
            isRanged
            showLegend={false}
            applyButton
            usePortal
            disabled={isRunning}
            formatStr="dd/MM/yyyy"
            placeholderString={t('Selecciona el periodo')}
            onApply={(dateRange: DateRangeObject) =>
              onPeriodChange(periodEdge.fromEmitted(dateRange))
            }
          />
        </div>

        <Divider />

        <div className="flex flex-col gap-2">
          {!result && (
            <>
              <Typography
                fontSize={FontSizeEnum.Md}
                weight={FontWeightEnum.Bold}
                color={FontColorEnum.Dark10}
              >
                {t('Se crearán {{count}} emisiones', { count: candidatesToCreate.length })}
              </Typography>

              {skippedCount > 0 && (
                <Typography fontSize={FontSizeEnum.Sm} color={FontColorEnum.NeutralSecondary}>
                  {t('{{count}} emisiones del periodo ya existen y no se volverán a crear', {
                    count: skippedCount,
                  })}
                </Typography>
              )}

              {hasPartiallyIncompleteSlots && (
                <Typography fontSize={FontSizeEnum.Sm} color={FontColorEnum.Warning}>
                  {t('{{count}} franjas sin hora de inicio o de fin no generan emisiones', {
                    count: slotsMissingHours.length,
                  })}
                </Typography>
              )}

              {duplicatesDropped > 0 && (
                <Typography fontSize={FontSizeEnum.Sm} color={FontColorEnum.NeutralSecondary}>
                  {t('{{count}} franjas coinciden en día y hora con otra, y solo se genera una', {
                    count: duplicatesDropped,
                  })}
                </Typography>
              )}
            </>
          )}

          {/* Not behind `result`: after a partial failure the batch refetches
              the lookup, and if that refetch fails the user needs the reason
              and the retry precisely then — otherwise the generate button is
              greyed out with nothing explaining it. */}
          {blockReason && (
            <Alert
              variant={
                blockReason === EGenerationBlockReason.LookupFailed
                  ? AlertVariantEnum.ERROR
                  : AlertVariantEnum.WARNING
              }
              withClose={false}
            >
              <div className="flex w-full items-center justify-between gap-4">
                <Typography fontSize={FontSizeEnum.Sm} color={FontColorEnum.Inherit}>
                  {blockMessages[blockReason]}
                </Typography>

                {blockReason === EGenerationBlockReason.LookupFailed && (
                  <Button
                    variant={ButtonVariantEnum.TEXT}
                    color={TextButtonColorEnum.DEFAULT}
                    size={ButtonSizeEnum.SM}
                    onClick={() => retryLookup()}
                  >
                    {t('Reintentar')}
                  </Button>
                )}
              </div>
            </Alert>
          )}
        </div>

        {isRunning && progress && (
          <div className="flex flex-col gap-2">
            <Typography fontSize={FontSizeEnum.Sm} color={FontColorEnum.NeutralSecondary}>
              {t('Creando emisión {{done}} de {{total}}', {
                done: progress.done,
                total: progress.total,
              })}
            </Typography>

            <ProgressBar progress={progressPercentage} />
          </div>
        )}

        {result && (
          <div className="flex flex-col gap-2">
            <Typography
              fontSize={FontSizeEnum.Md}
              weight={FontWeightEnum.Bold}
              color={FontColorEnum.Dark10}
            >
              {t('{{count}} emisiones creadas', { count: result.createdCount })}
            </Typography>

            {result.isCancelled && (
              <Alert variant={AlertVariantEnum.WARNING} withClose={false}>
                <Typography fontSize={FontSizeEnum.Sm} color={FontColorEnum.Inherit}>
                  {t(
                    'Has cancelado la generación: las emisiones restantes no se han creado. Puedes volver a generar el mismo periodo para crear las que faltan.'
                  )}
                </Typography>
              </Alert>
            )}

            {result.failed.length > 0 && (
              <Alert variant={AlertVariantEnum.ERROR} withClose={false}>
                <div className="flex flex-col gap-1">
                  <Typography fontSize={FontSizeEnum.Sm} color={FontColorEnum.Inherit}>
                    {t('{{count}} emisiones no se han podido crear:', {
                      count: result.failed.length,
                    })}
                  </Typography>

                  <ul className="list-inside list-disc">
                    {result.failed.map(({ candidate }) => (
                      <li key={candidate.startDatetime.toISOString()}>
                        <Typography fontSize={FontSizeEnum.Sm} color={FontColorEnum.Inherit}>
                          {describeCandidate(candidate)}
                        </Typography>
                      </li>
                    ))}
                  </ul>

                  <Typography fontSize={FontSizeEnum.Sm} color={FontColorEnum.Inherit}>
                    {t('Puedes volver a generar el mismo periodo: solo se crearán las que faltan.')}
                  </Typography>
                </div>
              </Alert>
            )}
          </div>
        )}

        {candidates.length > 0 && !result && (
          <Typography fontSize={FontSizeEnum.Sm} color={FontColorEnum.NeutralSecondary}>
            {t('El periodo y las franjas producen {{count}} emisiones en total', {
              count: candidates.length,
            })}
          </Typography>
        )}
      </div>
    </ModalDialog>
  );
}
